import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation, Navigate } from "react-router-dom";
import type { Location as RouterLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import SchoolSocialPlatform from './pages/school-social-platform';
import OnlineClassroom from './pages/online-classroom';
import ParentDashboard from './pages/parent-dashboard';
import HomePage from './pages/home';
import AuthenticationPortal from './pages/authentication-portal';
import StudentDashboard from './pages/student-dashboard';
import GradebookInterface from './pages/gradebook-interface';
import ClassManagementHub from './pages/class-management-hub';
import CommunicationCenter from './pages/communication-center';
import TeacherDashboard from './pages/teacher-dashboard';
import MyAccount from './pages/my-account';
import CalendarPage from './pages/calendar';
import NotificationsPage from './pages/notifications';
import LibraryPage from './pages/library';
import DocumentsPage from './pages/documents';
import SecretariatDashboard from './pages/secretariat-dashboard';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminDashboardGate from './components/auth/AdminDashboardGate';
import LegalFooter from './components/ui/LegalFooter';
import CookieConsentBanner from './components/legal/CookieConsentBanner';
import LegalAcceptanceModal from './components/legal/LegalAcceptanceModal';
import LegalOverlay from './components/legal/LegalOverlay';
import TermsAndConditions from './pages/legal/TermsAndConditions';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import CookiePolicy from './pages/legal/CookiePolicy';
import Gdpr from './pages/legal/Gdpr';
import CommercialInfo from './pages/legal/CommercialInfo';
import Dpa from './pages/legal/Dpa';
import PlatformRegulation from './pages/legal/PlatformRegulation';
import ContactPage from './pages/contact';
import AboutPage from './pages/about';
import SubscriptionsPage from './pages/subscriptions';
import TestPortalPage from './pages/test-portal';
import { useAuth } from './context/AuthContext';
import { roleToRedirectPath } from './lib/auth';

function isNativeCapacitorApp(): boolean {
  try {
    // `window.Capacitor` can exist even on web builds; use Capacitor helpers instead.
    if (typeof Capacitor?.isNativePlatform === "function") return Capacitor.isNativePlatform();
    if (typeof Capacitor?.getPlatform === "function") return Capacitor.getPlatform() !== "web";
    return false;
  } catch {
    return false;
  }
}

const SubscriptionsGate: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { me } = useAuth();
  if (me?.role === 'elev' && !Boolean(me?.over16)) {
    return <Navigate to="/student-dashboard" replace />;
  }
  return children;
};

const RootEntry: React.FC<{ isNativeApp: boolean }> = ({ isNativeApp }) => {
  const { me, isLoading } = useAuth();

  if (isLoading) return isNativeApp ? <AuthenticationPortal /> : <HomePage />;

  if (me?.role) {
    const target = roleToRedirectPath(me.role as any);
    return <Navigate to={target} replace />;
  }

  return isNativeApp ? <AuthenticationPortal /> : <HomePage />;
};

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const state = location.state as { backgroundLocation?: RouterLocation } | null;
  const backgroundLocation = state?.backgroundLocation;
  const isNativeApp = isNativeCapacitorApp();
  const hideFooter =
    location.pathname.startsWith('/communication-center') ||
    location.pathname.startsWith('/online-classroom') ||
    location.pathname.startsWith('/school-social-platform');

  return (
    <ErrorBoundary>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col">
        <div className="flex-1">
          <RouterRoutes location={backgroundLocation || location}>
            {/* Define your routes here */}
            <Route
              path="/"
              element={
                <RootEntry isNativeApp={isNativeApp} />
              }
            />

            <Route path="/authentication-portal" element={<AuthenticationPortal />} />

            {/* Standalone legal routes (direct access) */}
            <Route path="/legal/regulament-platforma" element={<PlatformRegulation />} />
            <Route path="/legal/termeni-si-conditii" element={<TermsAndConditions />} />
            <Route path="/legal/politica-de-confidentialitate" element={<PrivacyPolicy />} />
            <Route path="/legal/politica-de-cookies" element={<CookiePolicy />} />
            <Route path="/legal/gdpr" element={<Gdpr />} />
            <Route path="/legal/abonamente" element={<CommercialInfo />} />
            <Route path="/legal/dpa" element={<Dpa />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/test" element={<TestPortalPage />} />

            <Route
              path="/library"
              element={
                <ProtectedRoute allow={['teacher', 'admin', 'student', 'parent', 'secretariat']}>
                  <LibraryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents"
              element={
                <ProtectedRoute allow={['teacher', 'admin', 'student', 'parent', 'secretariat']}>
                  <DocumentsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/school-social-platform"
              element={
                <ProtectedRoute allow={['teacher', 'admin', 'student', 'parent', 'secretariat']}>
                  <SchoolSocialPlatform />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform-admin-dashboard"
              element={
                <ProtectedRoute allow={['admin']}>
                  <AdminDashboardGate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/secretariat-dashboard"
              element={
                <ProtectedRoute allow={['secretariat']}>
                  <SecretariatDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/online-classroom"
              element={
                <ProtectedRoute allow={['teacher', 'admin', 'student', 'parent']}>
                  <OnlineClassroom />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent-dashboard"
              element={
                <ProtectedRoute allow={['parent']}>
                  <ParentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/student-dashboard"
              element={
                <ProtectedRoute allow={['student']}>
                  <StudentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/gradebook-interface"
              element={
                <ProtectedRoute allow={['teacher', 'admin']}>
                  <GradebookInterface />
                </ProtectedRoute>
              }
            />
            <Route
              path="/class-management-hub"
              element={
                <ProtectedRoute allow={['teacher', 'admin']}>
                  <ClassManagementHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/communication-center"
              element={
                <ProtectedRoute allow={['teacher', 'admin', 'student', 'parent', 'secretariat']}>
                  <CommunicationCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute allow={['teacher', 'admin', 'student', 'parent', 'secretariat']}>
                  <CalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher-dashboard"
              element={
                <ProtectedRoute allow={['teacher']}>
                  <TeacherDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-account"
              element={
                <ProtectedRoute allow={['teacher', 'admin', 'student', 'parent', 'secretariat']}>
                  <MyAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute allow={['teacher', 'admin', 'student', 'parent']}>
                  <SubscriptionsGate>
                    <SubscriptionsPage />
                  </SubscriptionsGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute allow={['teacher', 'admin', 'student', 'parent', 'secretariat']}>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </div>
        {hideFooter ? null : <LegalFooter />}
      </div>
      <CookieConsentBanner />
      <LegalAcceptanceModal />

      {backgroundLocation && (
        <RouterRoutes>
          <Route
            path="/legal/regulament-platforma"
            element={
              <LegalOverlay title="Regulament platformă">
                <PlatformRegulation />
              </LegalOverlay>
            }
          />
          <Route
            path="/legal/termeni-si-conditii"
            element={
              <LegalOverlay title="Termeni și condiții">
                <TermsAndConditions />
              </LegalOverlay>
            }
          />
          <Route
            path="/legal/politica-de-confidentialitate"
            element={
              <LegalOverlay title="Politica de confidențialitate">
                <PrivacyPolicy />
              </LegalOverlay>
            }
          />
          <Route
            path="/legal/politica-de-cookies"
            element={
              <LegalOverlay title="Politica de cookies">
                <CookiePolicy />
              </LegalOverlay>
            }
          />
          <Route
            path="/legal/gdpr"
            element={
              <LegalOverlay title="GDPR">
                <Gdpr />
              </LegalOverlay>
            }
          />
          <Route
            path="/legal/abonamente"
            element={
              <LegalOverlay title="Abonamente & implementare">
                <CommercialInfo />
              </LegalOverlay>
            }
          />
          <Route
            path="/legal/dpa"
            element={
              <LegalOverlay title="Acord de împuternicit (DPA)">
                <Dpa />
              </LegalOverlay>
            }
          />
          <Route
            path="/contact"
            element={
              <LegalOverlay title="Contact">
                <ContactPage />
              </LegalOverlay>
            }
          />
          <Route
            path="/about"
            element={
              <LegalOverlay title="Despre noi">
                <AboutPage />
              </LegalOverlay>
            }
          />
        </RouterRoutes>
      )}
    </ErrorBoundary>
  );
};

const Routes: React.FC = () => (
  <BrowserRouter>
    <AppRoutes />
  </BrowserRouter>
);

export default Routes;
